import { useTranslation } from 'next-i18next'

import ArrowButton from '~/checklist/components/ArrowButton'
import SVGImage from '~/components/Image/SVGImage'
import useSsrSafeValue from '~/hooks/useSsrSafeValue'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { CurriculumEvents } from '~/analytics/curriculum/curriculumEvents.types'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'

import {
  selectFooterCurriculumDropdownExpanded,
  setFooterCurriculumDropdownExpandedActionCreator,
} from '../slice/curriculum.slice'
import { useGetInCurriculumV2 } from '../hooks/useGetCurriculumCountryCode'
import { getCurriculumDisplayLabel } from '../constants/usCurriculumStates.const'
import { getCurriculumFlagSrcKey } from '../utils/curriculumFlag.utils'

import CurriculumDropdownOptions from './CurriculumDropdownOptions'
import CurriculumUnsetFlag from './CurriculumUnsetFlag'

interface Props {
  handleOptionSelect: (chosenCurriculumName: string) => void
  currentCurriculum: string
  currentCurriculumCode: string
  unsetStateLabel?: string
  hideTitle?: boolean
}

export default function FooterCurriculumList({
  handleOptionSelect,
  currentCurriculum,
  currentCurriculumCode,
  unsetStateLabel,
  hideTitle,
}: Props): React.ReactElement {
  const { t } = useTranslation(['footer', 'curriculum', 'generics'])
  const dispatch = useAppDispatch()
  const isExpanded = useAppSelector(selectFooterCurriculumDropdownExpanded)
  const isCurriculumV2 = useGetInCurriculumV2()
  // The code comes from a cookie/geo lookup the server cannot do, so SSR always renders the International
  // flag. React patches text on hydration but not attributes, so the flag has to wait for a post-mount
  // render to correct itself — otherwise the label says Florida next to a globe.
  const ssrSafeCountryCode = useSsrSafeValue('', currentCurriculumCode)

  const expansionHandler = (): void => {
    const nextExpanded = !isExpanded
    curriculumAnalytics.logCurriculumClick(
      CurriculumEvents.CURRICULUM_COUNTRY_SELECTOR_TOGGLED,
      { surface: 'footer', is_expanded: nextExpanded },
      isCurriculumV2 ? 'v2' : 'v1',
    )
    dispatch(setFooterCurriculumDropdownExpandedActionCreator(nextExpanded))
  }

  return (
    <div className="w-full lg:w-70">
      {!hideTitle && <span className="ml-0.5 font-semibold text-white">{t('footerCurriculumTitle')}</span>}
      <button
        aria-expanded={isExpanded}
        className={`mt-1.5 w-full flex items-center justify-between gap-3 ${
          isExpanded ? 'rounded-t-2.5' : 'rounded-2.5'
        } border border-white bg-white-faint p-3`}
        onClick={expansionHandler}
        type="button"
      >
        <span className="flex min-w-0 items-center gap-3">
          {unsetStateLabel ? (
            <CurriculumUnsetFlag />
          ) : (
            <SVGImage
              alt={t('countryCode', { ns: 'generics' })}
              className="w-6 shrink-0"
              imageClassName="h-6 rounded-0.5"
              loading="eager"
              srcKey={getCurriculumFlagSrcKey(ssrSafeCountryCode, true)}
            />
          )}
          <span className="truncate text-sm text-white">
            {unsetStateLabel ?? getCurriculumDisplayLabel(currentCurriculum)}
          </span>
        </span>
        <ArrowButton color="text-white" expanded={isExpanded} size={20} />
      </button>
      <div
        className="overflow-hidden rounded-b-2.5 border-solid border-white bg-white text-gray-davy"
        style={{
          transition: `max-height 400ms cubic-bezier( 0.4, 0, 1, 1), border-width 400ms cubic-bezier( 1, 1, 1, 1)`,
          maxHeight: isExpanded ? '550px' : 0,
          borderWidth: isExpanded ? '1px' : '0px',
        }}
      >
        <CurriculumDropdownOptions
          currentCurriculum={currentCurriculum}
          currentCurriculumCode={currentCurriculumCode}
          handleOptionSelect={handleOptionSelect}
          isFooter
          isLightNested
        />
      </div>
    </div>
  )
}
